import { createContext, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import api, { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "../api/client";
import { SOUND_PACK_STORAGE_KEY, SOUND_PACK_SYNC_EVENT } from "../constants/audio";

import { initSyncManager } from '../services/syncManager';
import { readSession, emitSessionChange } from '../services/submissionSession';

const THEME_STORAGE_KEY = "rv-theme";

const emitThemeSyncEvent = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("rv-theme-sync"));
  }
};

const emitSoundPackSyncEvent = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SOUND_PACK_SYNC_EVENT));
  }
};

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readSession()?.profile || null);
  const [loading, setLoading] = useState(true);

  const applyUserPreferences = useCallback((profile) => {
    if (!profile) {
      return;
    }

    if (profile.preferredTheme) {
      const normalizedTheme = ({ light: 'light', dark: 'dark', greenblack: 'greenBlack', redblack: 'redBlack' })[String(profile.preferredTheme).toLowerCase()] || 'light';
      localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme);
      emitThemeSyncEvent();
    }

    if (profile.soundPack) {
      localStorage.setItem(SOUND_PACK_STORAGE_KEY, profile.soundPack);
      emitSoundPackSyncEvent();
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(SOUND_PACK_STORAGE_KEY);
    emitSoundPackSyncEvent();
    emitThemeSyncEvent();
    emitSessionChange();
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      api
        .get("/auth/me")
        .then((res) => {
          if (localStorage.getItem(TOKEN_STORAGE_KEY) !== token) return;
          setUser(res.data);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data));
          applyUserPreferences(res.data);
        })
        .catch((error) => {
          if (localStorage.getItem(TOKEN_STORAGE_KEY) !== token) return;
          if (error.response?.status === 401 || error.response?.status === 403) logout();
          // A network failure keeps the cached owner available for offline data entry.
          else setUser(readSession(token)?.profile || null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [applyUserPreferences, logout]);

  const login = useCallback((token, userData) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
    emitSessionChange();
    applyUserPreferences(userData);
    setLoading(false);
  }, [applyUserPreferences]);

  const refreshUser = useCallback(async () => {
    const res = await api.get("/auth/me");
    if (Number(res.data?.id) !== readSession()?.ownerUserId) throw new Error('Session changed while refreshing the profile.');
    setUser(res.data);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data));
    applyUserPreferences(res.data);
    return res.data;
  }, [applyUserPreferences]);

  useEffect(() => initSyncManager(() => localStorage.getItem(TOKEN_STORAGE_KEY)), []);
  useEffect(() => {
    const changed = () => setUser(readSession()?.profile || null);
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      <Fragment key={user?.id ?? "signed-out"}>{children}</Fragment>
    </AuthContext.Provider>
  );
}
